import React from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import { ButtonLinkIcon } from "@components/Button";
import Card from "@components/Card";
import Div from "@components/Div";
import Icon from "@components/Icon";
import { Text, TextMediumWeight, TextSemiBoldWeight } from "@components/Text";

import { CONSENT_STATUS, CONSENT_STATUS_TRANSLATION_KEYS } from "@utils/enum";

import { ConfirmConsentDialog } from "@pages/shared/orderDetails/components/ConsentStatusCard/components";

import { useConsentDetailsCard } from "./ConsentDetailsCard.hooks";

const ConsentDetailsCard = ({ consentDetails, onUpdateConsentDetails }) => {
  const { messages } = useIntl();

  const {
    handleCloseConsentDialog,
    handleConfirmConsent,
    handleOpenConsentDialog,
    isConsentDialogVisible,
  } = useConsentDetailsCard({
    candidateId: consentDetails?.candidateId,
    onUpdateConsentDetails,
  });

  return (
    <>
      {isConsentDialogVisible && (
        <ConfirmConsentDialog
          orderDetails={consentDetails}
          onClose={handleCloseConsentDialog}
          onConfirm={handleConfirmConsent}
        />
      )}

      <Card p={3} mb={3} borderRadius="10px">
        <Div display="flex" flexDirection="column" gridGap={2}>
          <Div
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Div display="flex" alignItems="center" gridGap={2}>
              <TextSemiBoldWeight>{messages.consent_title}</TextSemiBoldWeight>
            </Div>

            {consentDetails !== null &&
              consentDetails?.consentStatus === CONSENT_STATUS.PENDING && (
                <ButtonLinkIcon
                  px={0}
                  label={
                    <TextMediumWeight>{messages.label_edit}</TextMediumWeight>
                  }
                  onClick={handleOpenConsentDialog}
                  icon={
                    <Icon
                      name="pen"
                      mr={2}
                      color="var(--turquoise)"
                      fontSize="var(--fs-icon-s)"
                    />
                  }
                />
              )}
          </Div>

          <Div width={1} height={1} bg="var(--grey-lightest)" />

          <Div
            mt={2}
            display="flex"
            flexDirection={["column", "column", "row", "row"]}
            flexWrap={["nowrap", "nowrap", "wrap", "wrap"]}
            gridGap={3}
          >
            <Div display="flex" alignItems="center" flex="1 1 40%" gridGap={2}>
              <TextMediumWeight>{`${messages.consent_title}:`}</TextMediumWeight>

              <Text>
                {consentDetails !== null
                  ? messages.yes_label
                  : messages.no_label}
              </Text>
            </Div>

            {consentDetails !== null && (
              <Div
                display="flex"
                alignItems="center"
                flex="1 1 40%"
                gridGap={2}
              >
                <TextMediumWeight>
                  {`${messages.label_consent_status}:`}
                </TextMediumWeight>

                <Text>
                  {
                    messages[
                      CONSENT_STATUS_TRANSLATION_KEYS[
                        consentDetails?.consentStatus
                      ]
                    ]
                  }
                </Text>
              </Div>
            )}
          </Div>
        </Div>
      </Card>
    </>
  );
};

ConsentDetailsCard.propTypes = {
  consentDetails: PropTypes.oneOf([
    PropTypes.shape({
      orderId: PropTypes.string,
      candidateName: PropTypes.string,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          caseId: PropTypes.string,
        })
      ),
      consentStatus: PropTypes.string,
      candidateId: PropTypes.string,
    }),
    null,
  ]),
  onUpdateConsentDetails: PropTypes.func,
};

export default ConsentDetailsCard;
